import {
  QK_EMAIL_FILTER_COUNTS as FILTER_COUNTS_KEY,
  QK_SAVED_VIEW_COUNTS as SAVED_VIEW_COUNTS_KEY,
} from '@/hooks/email-cache-keys';
import { invalidateReportingAggregates } from '@/hooks/reporting-aggregate-invalidation';

import { PINNED_BAND_KEY } from './pinned-band-key';

import type { QueryClient } from '@tanstack/react-query';

/**
 * Centralized query keys for V2 correspondence hooks.
 * Hierarchical structure with 'correspondence' root enables broad invalidation:
 *   invalidateQueries({ queryKey: correspondenceKeys.all })
 * Matches existing commentsKeys and draftApprovalsKeys patterns.
 */
export const correspondenceKeys = {
  all: ['correspondence'] as const,
  index: (orgId: string) => [...correspondenceKeys.all, 'index', orgId] as const,
  indexFiltered: (orgId: string, filters: string, sort: string) =>
    [...correspondenceKeys.index(orgId), filters, sort] as const,
  detail: (orgId: string, corrId: string) =>
    [...correspondenceKeys.all, 'detail', orgId, corrId] as const,
  /** Timeline queries keyed by entityId (threadKey or correspondenceId) */
  timeline: (orgId: string, entityId: string, categories?: Set<string>) =>
    [
      ...correspondenceKeys.all,
      'timeline',
      orgId,
      entityId,
      categories ? [...categories].sort((a, b) => a.localeCompare(b)).join(',') : 'all',
    ] as const,
  /** Prefix-level key for invalidating all category variants for one timeline */
  timelineEntity: (orgId: string, entityId: string) =>
    [...correspondenceKeys.all, 'timeline', orgId, entityId] as const,
  /** Prefix-level key for invalidating all timelines in an org */
  timelineOrg: (orgId: string) => [...correspondenceKeys.all, 'timeline', orgId] as const,
  messengerReactionMutation: () => [...correspondenceKeys.all, 'messenger-reaction'] as const,
  /** Under the correspondence root so the realtime broad-invalidation guards
   *  (useIsMutating on that root) suppress refetches for the send's whole
   *  in-flight window — otherwise they wipe the optimistic timeline bubble. */
  messengerSendMutation: () => [...correspondenceKeys.all, 'send-messaging-draft'] as const,
  /** Prefix for every correspondence's draft history within an organization. */
  draftHistoryOrg: (orgId: string) => [...correspondenceKeys.all, 'draft-history', orgId] as const,
  /** Draft history (scrubber) — merged version stream for one correspondence. */
  draftHistory: (orgId: string, corrId: string) =>
    [...correspondenceKeys.draftHistoryOrg(orgId), corrId] as const,
  interactions: (orgId: string, corrId: string) =>
    [...correspondenceKeys.all, 'interactions', orgId, corrId] as const,
  shares: (orgId: string, corrId: string) =>
    [...correspondenceKeys.all, 'shares', orgId, corrId] as const,
  externalShares: (orgId: string, corrId: string) =>
    [...correspondenceKeys.all, 'external-shares', orgId, corrId] as const,
  capabilities: (orgId: string, corrId: string) =>
    [...correspondenceKeys.all, 'capabilities', orgId, corrId] as const,
  incomingShares: (orgId: string, userId: string) =>
    [...correspondenceKeys.all, 'incoming-shares', orgId, userId] as const,
  viewTracking: (orgId: string, threadKey: string) =>
    [...correspondenceKeys.all, 'views', orgId, threadKey] as const,
  readReceipts: (orgId: string, threadKey: string) =>
    [...correspondenceKeys.all, 'read-receipts', orgId, threadKey] as const,
  watchers: (orgId: string, corrId: string) =>
    [...correspondenceKeys.all, 'watchers', orgId, corrId] as const,
  /** Prefix key for invalidating all watching-count variants regardless of account filter */
  watchingCountPrefix: (orgId: string, userId: string) =>
    [...correspondenceKeys.all, 'watching-count', orgId, userId] as const,
  watchingCount: (
    orgId: string,
    userId: string,
    enabledAccountIds?: string[],
    enabledMessagingAccountIds?: string[],
    enabledExternalMailboxIds?: string[],
  ) =>
    [
      ...correspondenceKeys.watchingCountPrefix(orgId, userId),
      enabledAccountIds,
      enabledMessagingAccountIds,
      enabledExternalMailboxIds,
    ] as const,
  standalone: (orgId: string) => [...correspondenceKeys.index(orgId), 'standalone'] as const,
} as const;

/**
 * Authoritative per-correspondence pin-state read (ADR 0047), used by the detail
 * panel's actions menu. Scoped by user because the read is behind RLS — keying
 * without the user lets a later login in the same tab reuse the prior user's
 * cached `pinned_at`. Lives here (not beside the hook) so `usePinToggle` can
 * invalidate it without a circular import.
 */
export const correspondencePinStateKeys = {
  all: ['correspondence-pin-state'] as const,
  /** Org-scoped prefix, so a multi-org session refetches only the tenant whose
   *  pin actually changed rather than every cached org's pin state. */
  byOrg: (organizationId: string) => [...correspondencePinStateKeys.all, organizationId] as const,
  byId: (organizationId: string, correspondenceId: string, userId: string | undefined) =>
    [...correspondencePinStateKeys.byOrg(organizationId), correspondenceId, userId] as const,
} as const;

export function invalidateSavedViewCounts(
  queryClient: QueryClient,
  orgId: string | undefined,
): void {
  if (!orgId) {
    return;
  }
  queryClient.invalidateQueries({ queryKey: [SAVED_VIEW_COUNTS_KEY, orgId] }).catch(() => {
    // Best-effort cache refresh; callers should not fail because invalidation did.
  });
}

/**
 * Refresh the pinned band and the detail panel's authoritative pin-state read
 * after a lifecycle transition retired a pin (ADR 0047). `refetchType: 'active'`
 * because only the visible band needs to reconcile its membership.
 */
export function invalidatePinnedBand(queryClient: QueryClient, orgId: string | undefined): void {
  if (!orgId) {
    return;
  }
  queryClient
    .invalidateQueries({ queryKey: [PINNED_BAND_KEY, orgId], refetchType: 'active' })
    .catch(() => {
      // Best-effort cache refresh; callers should not fail because invalidation did.
    });
  // The detail panel's authoritative pin-state read is not one of the caches
  // above, so an open split-view panel reconciles too.
  queryClient
    .invalidateQueries({
      queryKey: correspondencePinStateKeys.byOrg(orgId),
      refetchType: 'active',
    })
    .catch(() => {
      // Best-effort cache refresh; callers should not fail because invalidation did.
    });
}

/**
 * Refresh the counter surfaces alone — filter buckets and saved-view badges.
 * Counter values are server-authoritative (docs/adr/0067): they are refreshed
 * by invalidation, never patched arithmetically on the client.
 *
 * Split out of {@link invalidateCorrespondenceCaches} so a caller that must not
 * pay the `['correspondence']` root invalidation can still keep the badges
 * honest.
 */
export function invalidateCorrespondenceCounters(
  queryClient: QueryClient,
  orgId: string | undefined,
): void {
  if (!orgId) {
    return;
  }
  queryClient.invalidateQueries({ queryKey: [FILTER_COUNTS_KEY, orgId] }).catch(() => {
    // Best-effort cache refresh; callers should not fail because invalidation did.
  });
  invalidateSavedViewCounts(queryClient, orgId);
}

/**
 * Invalidate the V2 correspondence VIEW caches only — the `['correspondence']`
 * root (timeline, inbox, detail, events) plus the reporting aggregates that
 * summarise correspondence rows server-side. Deliberately does NOT touch the
 * counter surfaces.
 *
 * Use this instead of {@link invalidateCorrespondenceCaches} from any hook that
 * already settles through `invalidateEmailCaches`, which owns
 * `email-filter-counts` and `saved-view-counts-*` (docs/adr/0067). Pairing the
 * two pokes both counter keys twice in the same tick: harmless at runtime
 * (TanStack dedupes concurrent refetches of one key) but it contradicts the
 * single-settlement-point model and hides which call is responsible.
 */
export function invalidateCorrespondenceViews(queryClient: QueryClient, orgId?: string): void {
  queryClient.invalidateQueries({ queryKey: correspondenceKeys.all }).catch(() => {
    // Best-effort cache refresh; callers should not fail because invalidation did.
  });
  // Reporting tiles aggregate correspondence rows server-side — mark them
  // stale alongside the list caches (outside the caller's orgId guard so
  // orgId-less callers still poke the unscoped prefix).
  invalidateReportingAggregates(queryClient, orgId);
}

/**
 * Invalidate all V2 correspondence queries AND the counter surfaces.
 * Call this from V2 mutation hooks in onSettled to keep timeline/events fresh.
 * The V1 invalidateEmailCaches utility handles V1 keys; this handles V2 keys.
 *
 * This is the right default: most callers have no other settlement point, so
 * the counter arm is what keeps their badges honest. Callers that DO route
 * through `invalidateEmailCaches` want {@link invalidateCorrespondenceViews}.
 */
export function invalidateCorrespondenceCaches(queryClient: QueryClient, orgId?: string): void {
  invalidateCorrespondenceViews(queryClient, orgId);
  invalidateCorrespondenceCounters(queryClient, orgId);
}

/**
 * Cancels in-flight email-index queries, then invalidates them so the next
 * read refetches fresh data. Awaiting cancel before invalidate prevents a
 * stale in-flight response from re-populating the cache between the two calls.
 * `useLiveCorrespondenceDerivedStatus` reads derived_status from this cache, so
 * anything that changes a row's status server-side ends with this call.
 */
export function refreshEmailIndex(
  queryClient: QueryClient,
  organizationId: string | undefined,
): void {
  const queryKey = ['email-index', organizationId];
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey }).catch(() => {
      // Best-effort — invalidation failure is non-critical
    });
  };
  queryClient
    .cancelQueries({ queryKey })
    .catch(() => {
      // Best-effort — cancel failure is non-critical
    })
    .finally(invalidate);
}
